// What creating a task group reads and sends (docs/12 "Task groups as built", "Sources as
// built"): the marketplace, the user's own secrets, Jira's projects, issue types, statuses and
// labels as the user's token sees them, a source preview, and the create itself. The source
// routes (S88) are written by hand until they reach the generated schema; the broker lists
// with the credential, so this process only ever names it by id.
import { queryOptions } from "@tanstack/react-query";

import type { Schemas } from "../../api/client";
import { requestJson } from "../../api/http";
import type { createBody, MarketplaceWorkflow } from "./draft";
import type { UpdateBody } from "./edit";

const enc = encodeURIComponent;

/** Workflows shown per page of the workflow step. */
export const WORKFLOW_PAGE = 15;

export interface WorkflowPage {
  items: MarketplaceWorkflow[];
  /** Whether another page follows (the request asks for one more than a page). */
  hasNext: boolean;
}

/** One page of a task type's workflows: best rated first, or best match first for `q`. */
export const marketplaceWorkflowsQuery = (taskTypeId: string, q = "", page = 0) =>
  queryOptions({
    queryKey: ["marketplace", "workflows", taskTypeId, q, page],
    queryFn: async (): Promise<WorkflowPage> => {
      const params = new URLSearchParams({ task_type: taskTypeId, limit: String(WORKFLOW_PAGE + 1), offset: String(page * WORKFLOW_PAGE) });
      if (q) params.set("q", q);
      else params.set("sort", "rating");
      const rows = (await requestJson("GET", `/api/marketplace/workflows?${params}`)) as MarketplaceWorkflow[];
      return { items: rows.slice(0, WORKFLOW_PAGE), hasNext: rows.length > WORKFLOW_PAGE };
    },
    enabled: taskTypeId !== "",
  });

export interface JiraProject {
  key: string;
  name: string;
}

export interface JiraFields {
  issue_types: { name: string; subtask: boolean }[];
  /** `category` is Jira's status category key: `new`, `indeterminate` or `done`. */
  statuses: { name: string; category: string }[];
}

const lookup = (credentialId: string, q?: string) => {
  const params = new URLSearchParams({ credential_id: credentialId });
  if (q) params.set("q", q);
  return params.toString();
};

export const jiraProjectsQuery = (credentialId: string, q: string) =>
  queryOptions({
    queryKey: ["task-sources", "jira", credentialId, "projects", q],
    queryFn: async () => (await requestJson("GET", `/api/task-sources/jira/projects?${lookup(credentialId, q)}`)) as JiraProject[],
    enabled: credentialId !== "",
    staleTime: 60_000,
  });

export const jiraFieldsQuery = (credentialId: string, project: string) =>
  queryOptions({
    queryKey: ["task-sources", "jira", credentialId, "fields", project],
    queryFn: async () =>
      (await requestJson("GET", `/api/task-sources/jira/projects/${enc(project)}/fields?${lookup(credentialId)}`)) as JiraFields,
    enabled: credentialId !== "" && project !== "",
    staleTime: 60_000,
  });

export const jiraLabelsQuery = (credentialId: string, q: string) =>
  queryOptions({
    queryKey: ["task-sources", "jira", credentialId, "labels", q],
    queryFn: async () => (await requestJson("GET", `/api/task-sources/jira/labels?${lookup(credentialId, q)}`)) as string[],
    enabled: credentialId !== "",
    staleTime: 60_000,
  });

export interface PreviewItem {
  ref: string;
  title: string;
  url: string | null;
  open: boolean;
  parent_ref: string | null;
}

export interface Preview {
  items: PreviewItem[];
  /** False when the source has more than the first page. */
  complete: boolean;
}

export interface PreviewRequest {
  source: string;
  source_config: Record<string, unknown>;
  source_credential_id: string;
}

/** List the source once with the chosen credential, as the poller would; nothing is stored. */
export const previewSource = async (body: PreviewRequest): Promise<Preview> =>
  (await requestJson("POST", "/api/task-sources/preview", body)) as Preview;

/**
 * `POST /api/task-groups`. Written by hand: the spec's `RepositoryDto` name is taken by the
 * workspace repository, so the generated body types `repositories` wrongly.
 */
export const createTaskGroup = async (body: ReturnType<typeof createBody>) =>
  (await requestJson("POST", "/api/task-groups", body)) as Schemas["TaskGroupDto"];

/** `PUT /api/task-groups/{id}`: every setting at once (S112). Hand-written for the same reason. */
export const updateTaskGroup = async (id: string, body: UpdateBody) =>
  (await requestJson("PUT", `/api/task-groups/${enc(id)}`, body)) as Schemas["TaskGroupDto"];
